import React, { useState, useEffect, useCallback } from "react";

import { useHistory, useParams } from "react-router-dom";
import { Box, Button, Flex } from "@chakra-ui/react";

import { Form, Formik } from "formik";
import { FocusError } from "../../../components/FocusError";
import * as Yup from "yup";
import Swal from "sweetalert2";

import { VisualizarPageEducacao } from "./Visualizar";
import { EducacaoCadastrar } from "./Cadastrar";
import CriancaService from "../../../../main/services/CriancaService";
import { Permission, criancaFunc } from "../../../../helpers/Funcionalidades";
import SemRegistros from "../../../components/SemRegistros";
import { useAppContext } from "../../../../Context/MessageContextProvider";
import { hasFormChanged } from "../../../utils/hasFormChanged";
import { useAuthProvider } from "../../../../Context/AuthProvider";
import { getBase64 } from "../../../components/Base64";
import { useLocation } from "react-router";
import { scrollToFieldAnexo } from "../../../utils/scrollToFIeldAnexo";

const suportedFormats = [
  "image/png",
  "image/bmp",
  "image/tiff",
  "image/jpg",
  "image/jpeg",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "application/msword",
  "application/pdf",
  "application/xml",
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.addin.macroEnabled.12",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const schema = Yup.object().shape({
  frequentaEscola: Yup.string().required("Este campo é obrigatório!"),
  idEscolaridade: Yup.object().when("frequentaEscola", {
    is: "S",
    then: Yup.object().required("Este campo é obrigatório!"),
    otherwise: Yup.object().nullable(),
  }),
  idTipoJustificativaEscolaridade: Yup.object()
    .nullable()
    .when("frequentaEscola", {
      is: "N",
      then: Yup.object().nullable().required("Este campo é obrigatório!"),
      otherwise: Yup.object().nullable(),
    }),
  descricaoJustificativa: Yup.string().when(
    "idTipoJustificativaEscolaridade.id",
    {
      is: 6,
      then: Yup.string().trim().required("Este campo é obrigatório!"),
      otherwise: Yup.string(),
    }
  ),
  educacaoEspecial: Yup.string().when("frequentaEscola", {
    is: "S",
    then: Yup.string().required("Este campo é obrigatório!"),
    otherwise: Yup.string().nullable(),
  }),
  unidadesEscolares: Yup.array().when("frequentaEscola", {
    is: "S",
    then: Yup.array()
      .of(
        Yup.object().shape({
          nome: Yup.string().trim().required("Este campo é obrigatório!"),
          escolaAtual: Yup.boolean(),
          matriculaAnexo: Yup.mixed()
            .nullable()
            .test("imageType", "Anexar uma extensão válida.", (value) => {
              if (value === null) {
                return true;
              } else {
                return suportedFormats.includes(value.type);
              }
            })
            .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
              if (value === null) {
                return true;
              } else {
                return value.size <= 10000000;
              }
            }),
        })
      )
      .test(
        "pelo-menos-um-escola-atual",
        "Selecione a escola atual.",
        (array) => {
          return (
            array && array.length > 0 && array.some((item) => item.escolaAtual)
          );
        }
      )
      .required("Pelo menos uma escola é obrigatória ")
      .min(1, "Pelo menos uma escola é obrigatória "),
    otherwise: Yup.array().of(
      Yup.object().shape({
        nome: Yup.string(),
        escolaAtual: Yup.boolean(),
      })
    ),
  }),
});

export const VisualizarEducacao = () => {
  const { id } = useParams();
  const [isTrueVisualizar, setIsTrueVisualizar] = useState(true);
  const [unidades, setUnidades] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idAnexo = searchParams.get("idAnexo");
  const idAba = searchParams.get("idAba");
  const idTipoPendencia = searchParams.get("idTipoPendencia");

  const url = window.location.href;
  const { roleKeycloack, user } = useAuthProvider();
  const {
    setEditarEducCrianca,
    setCadastrarEducCrianca,
    atualizarEducacao,
    setAtualizarEducacao,
    cadastrarEducacao,
    setCadastrarEducacao,
    setEditarCriancaCr,
    idPJ,
    radioClick,
    setRadioClick,
    setAnexarCrianca,
  } = useAppContext();

  const scrollToField = useCallback(
    (ref) => {
      scrollToFieldAnexo(idAnexo || idTipoPendencia, ref);
    },
    [idAnexo, idTipoPendencia]
  );

  useEffect(() => {
    setEditarCriancaCr(false);
    setAnexarCrianca(false);
  }, [setEditarCriancaCr, setAnexarCrianca]);

  useEffect(() => {
    if (url.includes("visualizar")) {
      setIsTrueVisualizar(true);
      setRadioClick("");
    }
  }, [url, setRadioClick]);

  useEffect(() => {
    if (url.includes("editar")) {
      setRadioClick("Editar");
    }
  }, [url, setRadioClick]);

  useEffect(() => {
    if (atualizarEducacao) {
      Swal.fire({
        title: "Sucesso!",
        text: "Registro atualizado com sucesso!",
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#169499",
      }).then((result) => {
        setAtualizarEducacao(false);
        setCadastrarEducacao(false);
      });
    } else if (cadastrarEducacao) {
      Swal.fire({
        title: "Sucesso!",
        text: "Registro cadastrado com sucesso!",
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#169499",
      }).then((result) => {
        setCadastrarEducacao(false);
        setAtualizarEducacao(false);
      });
    }
  }, [
    atualizarEducacao,
    setAtualizarEducacao,
    cadastrarEducacao,
    setCadastrarEducacao,
  ]);

  const [initialValues, setInitialValues] = useState({
    frequentaEscola: "",
    escolaridadeRepetida: "",
    educacaoEspecial: "",
    idEscolaridade: "",
    idTipoJustificativaEscolaridade: "",
    descricaoJustificativa: "",
    unidadesEscolares: [
      {
        nome: "",
        escolaAtual: true,
        idUnidadeEscolar: 0,
        matriculaAnexo: null,
      },
    ],
  });

  const onKeyDown = (keyEvent) => {
    if (keyEvent.code === "Enter" || keyEvent.code === "NumpadEnter") {
      keyEvent.preventDefault();
    }
  };

  const handleSubmit = (values, actions) => {
    setTimeout(async () => {
      const {
        frequentaEscola,
        educacaoEspecial,
        idEscolaridade,
        escolaridadeRepetida,
        idTipoJustificativaEscolaridade,
        descricaoJustificativa,
        unidadesEscolares,
      } = values;

      const unidadesEscolaresComArquivos = await Promise.all(
        unidadesEscolares?.map(async (unidadeEscolar) => {
          let arquivoBase64Value;

          if (unidadeEscolar?.matriculaAnexo instanceof File) {
            arquivoBase64Value = await new Promise((resolve) => {
              getBase64(unidadeEscolar?.matriculaAnexo, (base64Data) => {
                resolve(base64Data);
              });
            });
          } else {
            arquivoBase64Value = unidadeEscolar?.matriculaAnexo;
          }

          const nameImage = unidadeEscolar.matriculaAnexo?.name;
          const typeImage = unidadeEscolar.matriculaAnexo?.type;
          const idAnexoImage = unidadeEscolar.matriculaAnexo?.idAnexo;

          const prevMatriculaAnexo = {
            arquivoBase64: "",
            arquivoNome: "",
            arquivoTipo: "",
            atualizar: false,
            idAnexo: idAnexoImage ? idAnexoImage : 0,
            remover: true,
          };

          if (arquivoBase64Value && frequentaEscola === "S") {
            prevMatriculaAnexo.arquivoBase64 = arquivoBase64Value;
            prevMatriculaAnexo.arquivoNome = nameImage;
            prevMatriculaAnexo.arquivoTipo = typeImage;
            prevMatriculaAnexo.atualizar = idAnexoImage ? false : true;
            prevMatriculaAnexo.idAnexo = idAnexoImage ? idAnexoImage : 0;
            prevMatriculaAnexo.remover = false;
          }

          return {
            ...unidadeEscolar,
            idUnidadeEscolar: unidadeEscolar.id ?? 0,
            matriculaAnexo: prevMatriculaAnexo,
          };
        })
      );

      const escolasFormated = unidadesEscolaresComArquivos.map((item) => ({
        ...item,
        escolaAtual: item.escolaAtual ? "S" : "N",
      }));

      const valuesToSend = {
        unidadesEscolares:
          values.unidadesEscolares &&
          values.unidadesEscolares.length > 0 &&
          unidades !== false
            ? escolasFormated
            : escolasFormated,
        escolaridadeRepetida:
          frequentaEscola === "N" ? "N" : escolaridadeRepetida,

        frequentaEscola: frequentaEscola,
        educacaoEspecial:
          values.frequentaEscola === "S" ? educacaoEspecial : "",
        idEscolaridade: values.frequentaEscola === "S" ? idEscolaridade.id : "",
        idTipoJustificativaEscolaridade:
          values.frequentaEscola === "N"
            ? idTipoJustificativaEscolaridade.id
            : "",
        descricaoJustificativa:
          values.frequentaEscola === "N" &&
          values.idTipoJustificativaEscolaridade.id === 6
            ? descricaoJustificativa
            : "",
      };

      if (isTrueVisualizar) {
        setRadioClick("Editar");
        setIsTrueVisualizar(!isTrueVisualizar);
        history.push(`/crianca/editar-educacao/${id}`);
      } else {
        if (radioClick === "Editar") {
          CriancaService.putCriancaEducacao(id, valuesToSend)
            .then((res) => {
              history.push(`/crianca/visualizar-educacao/${id}`);
              setIsTrueVisualizar(true);
              setRadioClick("");

              setCadastrarEducCrianca(false);
              setEditarEducCrianca(false);
              setAtualizarEducacao(true);
            })
            .catch((error) => {
              console.log(error.response.data);
              Swal.fire({
                title: "Atenção!",
                text: error.response.data.message
                  ? error.response.data.message
                  : "",
                icon: "error",
                confirmButtonText: "Fechar",
                confirmButtonColor: "#169499",
              });
            });
        } else {
          CriancaService.postCriancaEducacao(id, valuesToSend)
            .then((res) => {
              history.push(`/crianca/visualizar-educacao/${id}`);
              setIsTrueVisualizar(true);
              setRadioClick("");
              setCadastrarEducCrianca(false);
              setEditarEducCrianca(false);

              setCadastrarEducacao(true);
            })
            .catch((error) => {
              console.log(error.response.data);
              Swal.fire({
                title: "Atenção!",
                text: error.response.data.message
                  ? error.response.data.message
                  : "",
                icon: "error",
                confirmButtonText: "Fechar",
                confirmButtonColor: "#169499",
              });
            });
        }
      }

      actions.setSubmitting(false);
    });
  };

  return roleKeycloack &&
    (roleKeycloack?.includes(criancaFunc.visualizarEducacao) ||
      ((roleKeycloack?.includes(criancaFunc.visualizarTudoAcolhimento) ||
        roleKeycloack?.includes(criancaFunc.visualizarTudoTutelar)) &&
        user?.orgaoExterno?.includes(idPJ))) ? (
    <>
      <Flex flex={1} flexDir="column" alignItems="center" overflow="auto">
        <Flex flexDir="column" w="100%">
          <Formik
            validationSchema={schema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
          >
            {(props) => (
              <Form onKeyDown={onKeyDown}>
                {setEditarEducCrianca(
                  hasFormChanged(initialValues, props.values)
                )}
                <FocusError />
                {isTrueVisualizar ? (
                  <VisualizarPageEducacao
                    id={id}
                    setIsTrueVisualizar={setIsTrueVisualizar}
                    initialValues={initialValues}
                    setInitialValues={setInitialValues}
                    setUnidades={setUnidades}
                    url={url}
                    idAnexo={idAnexo}
                    idAba={idAba}
                    idTipoPendencia={idTipoPendencia}
                  />
                ) : roleKeycloack &&
                  (roleKeycloack.includes(criancaFunc.incluirEducacao) ||
                    ((roleKeycloack?.includes(
                      criancaFunc.visualizarTudoAcolhimento
                    ) ||
                      roleKeycloack?.includes(
                        criancaFunc.visualizarTudoTutelar
                      )) &&
                      user?.orgaoExterno?.includes(idPJ))) ? (
                  <EducacaoCadastrar
                    initialValues={initialValues}
                    valuesProps={props.values}
                    idAnexo={idAnexo}
                    idTipoPendencia={idTipoPendencia}
                    scrollToField={scrollToField}
                  />
                ) : (
                  <Box mt="2">
                    <SemRegistros buttonVisible={true} />
                  </Box>
                )}
                <Flex justifyContent="flex-end">
                  <Permission
                    roleKeycloack={roleKeycloack}
                    func={
                      isTrueVisualizar
                        ? criancaFunc.alterarEducacao
                        : criancaFunc.incluirEducacao
                    }
                    user={user}
                    id={idPJ}
                    visualizar={true}
                  >
                    <Button
                      type="submit"
                      translate="no"
                      mt="30px"
                      px="45px"
                      h="42px"
                      color="#FFF"
                      backgroundColor="#169499"
                      fontWeight={700}
                      fontSize="15px"
                      lineHeight="21.6px"
                      _hover={{ bg: "#169499" }}
                      isLoading={props.isSubmitting}
                    >
                      {isTrueVisualizar ? "Editar" : "Salvar"}
                    </Button>
                  </Permission>
                </Flex>
              </Form>
            )}
          </Formik>
        </Flex>
      </Flex>
    </>
  ) : (
    <Box mt={2}>
      <SemRegistros buttonVisible={true} half />
    </Box>
  );
};
